import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  DialogTitle,
} from "@mui/material";
import {
  IconButton,
  Typography,
  Box,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import api from "api";
import CodeListSection from "components/shared/CodeListSection";
import { debounce } from "lodash";

const ProcedureCodeDialog = ({
  open,
  onClose,
  onSave,
  existingCodes = [],
  readOnly = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const handleSearchChange = (event, value, reason) => {
    if (event) {
      if (reason === "input" || reason === "clear") {
        setSearchTerm(value);
      }
    }
    if (!value) {
      setNoResultsFound(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event && event.key === "Enter") {
      event.preventDefault(); // Prevent default Enter key behavior
      if (searchTerm.length >= 2) {
        performSearch(); // Trigger the search
        // setDropdownOpen(true); // Optionally open the dropdown
      } else {
        setDropdownOpen(false);
      }
    }
  };

  const performSearch = React.useMemo(
    () =>
      debounce(async () => {
        if (searchTerm.length === 0 || searchTerm.length < 1) {
          setNoResultsFound(false);
        }
        if (searchTerm.length < 2) return;
        setLoading(true);
        setNoResultsFound(false);
        try {
          const response = await api.procedureCode.getProcedureCodeList(
            searchTerm,
            "CODE"
          );
          const mappedResults = response.data.listDto.map((item) => ({
            code: item.procedureCode,
            description: item.procedureShortDescription,
          }));

          setSearchResults(mappedResults);
          setDropdownOpen(mappedResults.length > 0);
          if (searchTerm.length === 0) {
            setNoResultsFound(false);
          } else {
            setNoResultsFound(mappedResults.length === 0);
          }
        } catch (error) {
          console.error("Error fetching procedure codes:", error);
          setLoading(false);
        } finally {
          setLoading(false);
        }
      }, 500),
    [searchTerm]
  );
  useEffect(() => {
    performSearch();
  }, [performSearch]);

  useEffect(() => {
    return () => {
      performSearch.cancel();
    };
  }, [performSearch]);

  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setSearchResults([]);
      setSelectedCodes(existingCodes);
      setNoResultsFound(false);
    }
  }, [open, existingCodes]);
  const handleChange = (_, value, reason) => {
    if (value && !selectedCodes.filter((s) => s.code === value.code).length) {
      if (reason === "selectOption") {
        setSelectedCodes((prev) => [...prev, value]);
        // setSearchTerm('');
        setDropdownOpen(false);
      }
    }
  };

  const handleDelete = (index) => {
    setSelectedCodes((currentList) =>
      currentList.filter((_, i) => i !== index)
    );
  };

  const handleClose = () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedCodes([]);
    onClose();
  };
  const handleAddCodeList = () => {
    onSave(selectedCodes);
    handleClose(); // Close the dialog
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h4" sx={{ fontSize: "1.5rem" }}>
            {readOnly ? "Procedure Codes" : "Procedure Code Search"}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {!readOnly && (
          <>
            <Typography variant="body">
              Type two or more characters to search for and add procedure
              code(s) to policy.
            </Typography>
            <Autocomplete
              freeSolo
              filterOptions={(x) => x}
              options={searchResults}
              getOptionLabel={(option) =>
                option ? `${option.code} - ${option.description}` : ""
              }
              inputValue={searchTerm}
              onInputChange={handleSearchChange}
              onChange={handleChange}
              open={dropdownOpen}
              onOpen={() => setDropdownOpen(true)}
              onClose={() => setDropdownOpen(false)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Procedure Code or Description Search"
                  margin="normal"
                  variant="outlined"
                  onKeyDown={handleKeyDown}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {noResultsFound && (
              <Typography color="error" align="center">
                No matching code found.
              </Typography>
            )}
          </>
        )}
        <CodeListSection
          selectedCodes={selectedCodes}
          handleDelete={handleDelete}
          readOnly={readOnly}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {!readOnly && (
          <Button onClick={handleAddCodeList} color="primary">
            Add Code List
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProcedureCodeDialog;
